/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import React, { useEffect, useState } from "react";
import { useQuery, useMutation, ApolloError } from "@apollo/client";
import { navigate } from "@reach/router";
import NotFound from "pages/not-found";
import { ContainerCard } from "components/haven-design";

import { useViewer } from "components/viewer/context";
import Spinner from "components/spinner";
import {
  EligibilityFormContext,
  EligibilityFormContextVariables,
  TrackFirstOpening,
  TrackFirstOpeningVariables,
  LoginMethod,
} from "types/gql-types";
import Header from "components/header";
import { initializeUser } from "services/analytics";
import { LoadingSpinner } from "components/graphql-views";
import trackFirstOpeningMutation from "./query/track-first-opening.gql";
import formContext from "./query/form-context.gql";

import FormSection from "./components/form";
import FormError from "./components/form-render-error";

const subject = "Accessing the form";
const body = `Hi [property],
Can you resend me the form?
I received this error message from Haven Connect and I tried to log in but I had the wrong login.
What user is my application connected to?
You should be able to see this if you search my application.
Thank you!`;
const EmailWrapper: React.FC<{email: string, property: string}> = ({ email, property }) =>
  <a href={`mailto:${email}?subject=${encodeURIComponent(subject)
  }&body=${encodeURIComponent(body.replace("[property]", property))}`}>{email}</a>;
const TelWrapper: React.FC<{phone: string}> = ({ phone }) => <a href={`tel:${phone}`}>{phone}</a>;

interface Props {
  path: string;
  submissionId?: string;
}

interface ContextProps {
  loading?: boolean;
  data: EligibilityFormContext | undefined;
  error?: ApolloError;
}
const FormRenderError: React.FC<{message: React.ReactNode}> = (props) =>
  <div role="main">
    <Header />
    <ContainerCard>
      <FormError {...props} />
    </ContainerCard>
  </div>;

const Form: React.FC<ContextProps> = ({ loading, error, data }) => {

  if (loading) return <Spinner size={25} center />;
  if (error) return <FormRenderError message="No Form data available" />;
  if (!data || !data.submission?.form) {
    return (
      <div role="main">
        <Header />
        <ContainerCard>
          <NotFound default />
        </ContainerCard>
      </div>
    );
  }
  const { submission: { form: { id, open }, submittedAt } } = data;

  if (!open) {
    navigate(`/form/${id}`);
    return null;
  }

  if (submittedAt !== null) {
    navigate("/form-completed", { replace: true });
    return null;
  }
  return <FormSection submission={data.submission} />;
};

const Page: React.FC<Props> = ({ submissionId }) => {
  if (typeof submissionId === "undefined") {
    throw new Error("component must provide a submission ID");
  }
  const [sentFirstOpening, setSentFirstOpening] = useState(false);
  const [trackFirstOpening] = useMutation<TrackFirstOpening, TrackFirstOpeningVariables>(
    trackFirstOpeningMutation,
    { refetchQueries: ["AppliedPropertyFeatures"] },
  );
  const viewer = useViewer();
  const context = useQuery<EligibilityFormContext, EligibilityFormContextVariables>(formContext, {
    variables: { submissionId },
  });
  useEffect(() => {
    if (viewer?.id && context.data?.submission?.form) {
      const roles = Object.values(viewer.logins[0]?.roles || [""]);
      const email = viewer.logins[0]?.method === LoginMethod.EMAIL ? viewer.logins[0].username : "";
      initializeUser({
        userId: viewer.id,
        fullName: "",
        roles,
        email,
        organizationId: context.data.submission?.form.property?.organizationId || "",
        propertyId: context.data.submission?.form.property?.id || "",
        propertyName: context.data.submission?.form.property?.name || "",
      });
    }
  }, [viewer, context]);
  // Track first opening
  useEffect(() => {
    if (
      !sentFirstOpening &&
      viewer &&
      context.data?.submission &&
      !context.data?.submission?.firstOpenedAt
    ) {
      setSentFirstOpening(true);
      trackFirstOpening({ variables: { submissionId } });
    }
  }, [viewer, submissionId, context]);
  useEffect(() => {
    context.refetch({ submissionId });
  }, [viewer]);
  if (context.loading) return <LoadingSpinner />;
  if (context.error || !context.data || !context.data.submission) {
    return <FormRenderError message="Form submission data not found"/>;
  }
  const userId = context.data.submission?.interest?.userId;
  const { property } = context.data.submission.form;
  const { userId: submissionUserId } = context.data.submission;

  const permissionErrorMessage = (<>
    Application is associated with a different user account.
    Please log in with the correct user account.
    If you do not know the correct user account,
    contact {property.name} at <EmailWrapper email={property.email} property={property.name}/>
    &nbsp;or <TelWrapper phone={property.phone} />
  </>);

  if (viewer && ((
    userId && userId !== viewer.id
  ) || (
    submissionUserId && submissionUserId !== viewer.id
  ))) {
    return <FormRenderError message={permissionErrorMessage}/>;
  }
  return <Form {...context} />;
};

export default Page;
